import { FLARES, SMOKE_TRAILS, ENV } from '../core/SimConfig.js';
import { rand } from '../utils.js';

// ── FlareSystem ─────────────────────────────────────────────────────────────
//
// Cosmetic-only countermeasure flares. Each live flare:
//   - integrates a simple ballistic path (gravity + linear air drag),
//   - emits `flare` particles every frame via the shared ParticleSystem,
//   - owns a `flare` smoke-trail handle from the shared SmokeTrailSystem.
//
// Trigger logic (rising-edge timing, dispenser nodes, eject velocity) lives
// in the caller — see CountermeasureDispenser. This system just integrates
// and renders flares spawned via `dispense()`.

export default class FlareSystem {
  /**
   * @param {{
   *   scene: THREE.Scene,
   *   particleSystem: import('./ParticleSystem.js').default,
   *   smokeTrailSystem: import('./SmokeTrailSystem.js').default,
   * }} opts
   */
  constructor({ scene, particleSystem, smokeTrailSystem }) {
    this.scene = scene;
    this.particleSystem = particleSystem;
    this.smokeTrailSystem = smokeTrailSystem;

    // ── Struct-of-Arrays pool ────────────────────────────────────────
    const N = FLARES.poolSize;
    this.poolSize = N;
    this.posX     = new Float32Array(N);
    this.posY     = new Float32Array(N);
    this.posZ     = new Float32Array(N);
    this.velX     = new Float32Array(N);
    this.velY     = new Float32Array(N);
    this.velZ     = new Float32Array(N);
    this.age      = new Float32Array(N);
    this.lifetime = new Float32Array(N);
    this.trailHandle = new Int32Array(N);     // -1 when no trail
    this.alive    = new Uint8Array(N);
  }

  /**
   * Spawn one flare at a world-space position with an initial world-space
   * velocity. Caller decides cadence and origin (per-aircraft dispenser node,
   * AI body origin, etc.).
   */
  dispense(x, y, z, vx, vy, vz) {
    let slot = -1;
    for (let i = 0; i < this.poolSize; i++) {
      if (!this.alive[i]) { slot = i; break; }
    }
    if (slot === -1) return;

    const spread = FLARES.ejectSpread;
    this.posX[slot] = x;
    this.posY[slot] = y;
    this.posZ[slot] = z;
    this.velX[slot] = vx + (Math.random() * 2 - 1) * spread;
    this.velY[slot] = vy + (Math.random() * 2 - 1) * spread;
    this.velZ[slot] = vz + (Math.random() * 2 - 1) * spread;
    this.age[slot] = 0;
    this.lifetime[slot] = rand(FLARES.burnTime[0], FLARES.burnTime[1]);
    this.alive[slot] = 1;

    this.trailHandle[slot] = this.smokeTrailSystem
      ? this.smokeTrailSystem.spawn(SMOKE_TRAILS.presets.flare)
      : -1;
  }

  /**
   * Per-frame tick. Integrates live flares, emits particles, advances
   * smoke-trail handles, prunes burned-out flares.
   *
   * @param {number} dt
   * @param {THREE.Vector3} [wind] — world-space wind velocity
   */
  update(dt, wind) {
    const g = ENV.gravity;
    const dragK = FLARES.drag;
    const windX = wind ? wind.x : 0;
    const windY = wind ? wind.y : 0;
    const windZ = wind ? wind.z : 0;
    // Frame-rate-corrected exponential drag against air-relative velocity.
    const dragFactor = Math.exp(-dragK * dt);

    for (let i = 0; i < this.poolSize; i++) {
      if (!this.alive[i]) continue;

      this.age[i] += dt;
      if (this.age[i] >= this.lifetime[i]) {
        this._kill(i);
        continue;
      }

      // Air-relative velocity → exponential drag → re-add wind.
      const vrx = (this.velX[i] - windX) * dragFactor + windX;
      const vry = (this.velY[i] - windY) * dragFactor + windY;
      const vrz = (this.velZ[i] - windZ) * dragFactor + windZ;
      this.velX[i] = vrx;
      this.velY[i] = vry - g * dt;
      this.velZ[i] = vrz;

      this.posX[i] += this.velX[i] * dt;
      this.posY[i] += this.velY[i] * dt;
      this.posZ[i] += this.velZ[i] * dt;

      // Emit one `flare` particle per frame.
      if (this.particleSystem) {
        this.particleSystem._emit(
          'flare',
          this.posX[i], this.posY[i], this.posZ[i],
          this.velX[i] * 0.2, this.velY[i] * 0.2, this.velZ[i] * 0.2,
        );
      }

      // Advance smoke trail head to current flare position.
      if (this.smokeTrailSystem && this.trailHandle[i] >= 0) {
        this.smokeTrailSystem.advance(
          this.trailHandle[i],
          this.posX[i], this.posY[i], this.posZ[i],
          this.velX[i], this.velY[i], this.velZ[i],
        );
      }
    }
  }

  _kill(i) {
    this.alive[i] = 0;
    if (this.smokeTrailSystem && this.trailHandle[i] >= 0) {
      this.smokeTrailSystem.release(this.trailHandle[i]);
    }
    this.trailHandle[i] = -1;
  }

  // ── Lifecycle ────────────────────────────────────────────────────────────

  dispose() {
    for (let i = 0; i < this.poolSize; i++) {
      if (this.alive[i]) this._kill(i);
    }
  }
}
